import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';
import { applyModuleSeo } from '../../utils/seo';

const CANONICAL = 'https://explanatum.co.uk/modules/pricing-intelligence';

const PricingIntelligenceLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI Pricing Intelligence Suite | Explanatum';
    const desc = 'Model prices, margins, and go-to-market strategies with AI workflows that beat spreadsheet errors and consultant delays.';
    applyModuleSeo({
      title,
      description: desc,
      canonicalUrl: CANONICAL,
      keywords: [
        'pricing intelligence software',
        'price optimization tool',
        'margin analysis platform',
        'ai pricing strategy',
        'explanatum pricing suite'
      ],
      structuredDataId: 'ld-module-pricing-intelligence',
      softwareName: 'AI Pricing Intelligence Suite',
      softwareCategory: 'FinanceApplication'
    });
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="Pricing intelligence overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-semibold text-blue-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                AI Pricing Intelligence Suite
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Confident pricing without spreadsheet roulette.
                <span className="mt-2 block text-blue-600">
                  Calculate, optimize, and narrate price strategy in one hub.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Generate cost-plus recommendations, interrogate product margins, and draft AI-assisted pricing playbooks. Explanatum replaces brittle workbooks and agency retainers with an always-on pricing desk for operators.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_pricing_intelligence_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 via-emerald-500 to-cyan-500 px-6 py-3 text-base font-semibold text-white shadow-lg transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/finance"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Open pricing workspace
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Cost, margin, and competitor inputs captured once
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  AI pricing narratives & go-to-market recommendations
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-cyan-300" />
                  Exportable reports and alerts when margin dips
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Pricing dashboard preview">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-indigo-500/30 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-emerald-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Pricing HQ</span>
                  <span>Live signals</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Scenario: Premium SaaS bundle • Recommended price £89/mo • Margin 68% • Competitor median £75/mo.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Gross margin alerts: Hardware kit dropped below 32% → AI suggests supplier negotiation + price ladder.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Strategy memo ready: Penetration play for DACH region with rollout steps and enablement assets.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Key benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">One command center for price, margin, and strategy</h2>
            <p className="mt-4 text-base text-gray-500">Transform ad-hoc pricing requests into repeatable workflows that keep revenue, finance, and GTM aligned.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Price Calculator',
              body: 'Model cost-plus, competitive, or premium pricing instantly and store every scenario for future iterations.'
            }, {
              title: 'Profit Margin Analyzer',
              body: 'Track gross and net margins per product, benchmark against industry averages, and surface unhealthy units fast.'
            }, {
              title: 'Strategy Advisor',
              body: 'Use AI to craft pricing narratives, positioning notes, and step-by-step implementation plans per launch.'
            }, {
              title: 'Collaborative workspace',
              body: 'Share saved scenarios with leadership, export CSVs, or plug insights into CRM and BI tooling.'
            }, {
              title: 'Alerts & automations',
              body: 'Trigger Slack/WhatsApp alerts when margin thresholds are breached or when a scenario is approved.'
            }, {
              title: 'Consultant-level reporting',
              body: 'Create internal decks and investor summaries in minutes—without waiting on outside agencies.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why teams move off spreadsheets and high-fee consultants</h2>
            <p className="mt-4 text-base text-gray-500">Stay agile when the market shifts, keep institutional knowledge in-house, and make confident pricing calls every day.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Pricing Intelligence Suite',
              points: [
                'Cost, margin, and strategy data in a single workspace',
                'AI-assisted recommendations with repeatable playbooks',
                'Scenario cloning for region, segment, or product iterations',
                'Team access, alerts, and exports included by default',
                'Predictable subscription without hourly billing'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Manual spreadsheets',
              points: [
                'Complex formulas break when assumptions change',
                'Version control issues slow collaboration and approvals',
                'No automated alerts—problems surface after margin erosion',
                'Difficult to tie pricing changes to strategy documentation',
                'Time lost stitching numbers into board-ready narratives'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Pricing consultants & agencies',
              points: [
                '£4,000+ retainers for periodic analysis and decks',
                'Slow turnarounds when market conditions shift',
                'Insights locked in slideware and not operational systems',
                'Knowledge leaves when the engagement ends',
                'Limited access for wider team without extra fees'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-indigo-400/30 bg-indigo-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Need new pricing for next week’s launch?”</h3>
            <p className="mt-3 text-base text-gray-600">Spin scenarios, validate margins, and deliver a complete pricing playbook tonight—without chasing consultants or rebuilding models.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_pricing_intelligence_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 via-emerald-500 to-cyan-500 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-indigo-400/30 hover:scale-[1.02]"
              >
                Launch pricing now
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with AI Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where pricing intelligence pays back fast</h2>
            <p className="mt-4 text-base text-gray-500">From subscription launches to retail bundles, your revenue plan stays data-backed and synchronized.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'SaaS & digital products',
              body: 'Experiment with plan tiers, usage pricing, and expansion offers while keeping CAC and margin targets in view.'
            }, {
              title: 'Retail & DTC brands',
              body: 'Align cost changes, promotions, and bundle pricing with automated margin checks and channel guidance.'
            }, {
              title: 'Consultancies & agencies',
              body: 'Craft retainer and value-based pricing frameworks with AI-authored proposals ready for clients.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default PricingIntelligenceLanding;
